import { CreativeDataNode } from '@creative/nodes/data-nodes';
import { ElementSelection } from '@creative/nodes/selection';
import { RichText } from '@creative/elements/rich-text/rich-text';
import { SVGBackground } from '@creative/svg-background';
import { IVideoRenderer } from '@creative/elements/video/video-renderer.header';
import { IVideoAsset, IVideoSettings } from '@creative/elements/video/video';
import { IAction, IActionListener } from './action';
import { IAnimation } from './animation';
import { IColor } from './color';
import { IPosition, ISize } from './dimension';
import { IFeed } from './feed';
import { IFontStyle } from './font';
import { IImageAsset, IImageSettings } from './image';
import { IState } from './state';
import { AppearenceStyles, IBorder, IFilter, IFilterMap, IPadding, IShadow, ITextShadow } from './style';
import { HorizontalAlignment, ICharacterProperties, IText, ITextElementProperties, TextOverflow, VerticalAlignment } from './text';
import { IWidgetElementDataNode, IWidgetViewElement } from './widget';
import { IGuideline } from './workspace';

export interface INodeId {
    id: string;
    cidIndex?: number;
    parentId?: string;
}

export interface IId {
    id: string;
}

export interface ICreativeViewNode extends ISVGBackgroundNode<NodeKind.Creative>, ISize {
    readonly elements: OneOfViewElements[];
}

export interface ICreativeDataNode {
    id: string;
    kind: NodeKind.Creative;
    fill?: IColor;
    border?: IBorder;
    radius: number;
    width: number;
    height: number;
    elements: OneOfElementDataNodes[];
    guidelines?: IGuideline[];
    startTime?: number;
    stopTime?: number;
    loops: number;
}

export interface ISVGBackground {
    render_m: <Kind extends NodeKind>(element: ISVGBackgroundNode<Kind>, overrides?: AppearenceStyles) => Promise<void> | undefined;
}

export interface IGroupViewElement extends IElementViewNode<NodeKind.Group>, IChildren {}

export interface IRootNode extends IDataChildren, NodeWithKind<NodeKind.Creative> {}

export interface IChildren {
    elements: OneOfViewElements[];
}

export interface IDataChildren {
    elements: OneOfElementDataNodes[];
}

export enum NodeName {
    Rectangle = 'Rectangle',
    Ellipse = 'Ellipse',
    Text = 'Text',
    Button = 'Button',
    Image = 'Image',
    Widget = 'Widget',
    Video = 'Video',
}

export enum NodeKind {
    Creative = 'creative',
    Rectangle = 'rectangle',
    Text = 'text',
    Image = 'image',
    Video = 'video',
    Group = 'group',
    Selection = 'selection',
    AnyElement = 'any',
    Ellipse = 'ellipse',
    Button = 'button',
    Widget = 'widget',
    BannerflowLibraryWidget = 'bannerflowLibraryWidget', // Only when bfl-widget is in bl
    BannerflowLibraryWidgetInstance = 'bannerflowLibraryWidgetInstance', // Only when bfl-widget is on canvas
}

export enum NodeKindIcon {
    Creative = 'creative',
    Rectangle = 'shape-rectangle',
    Text = 'text',
    Image = 'image',
    Group = 'group',
    Selection = 'selection',
    AnyElement = 'any',
    Ellipse = 'shape-oval',
    Button = 'button',
    Widget = 'widget',
    BannerflowLibraryWidget = 'widget',
    BannerflowLibraryWidgetInstance = 'widget'
}

export type OneOfViewElements =
    IEllipseViewElement |
    IRectangleViewElement |
    IButtonViewElement |
    ITextViewElement |
    IImageViewElement |
    IGroupViewElement |
    IWidgetViewElement |
    IVideoViewElement |
    IAnyViewElement;

export type OneOfSelectableElements =
    OneOfElementDataNodes |
    ElementSelection;

// Is this union type needed?
export type OneOfGroupViewNodes =
    IGroupViewElement |
    ICreativeViewNode;

export type OneOfGroupDataNodes =
    IGroupElementDataNode |
    IRootNode;

export type OneOfTextViewElements =
    IButtonViewElement |
    ITextViewElement;

export type OneOfTextDataNodes =
    IButtonElementDataNode |
    ITextElementDataNode;

export interface ICreativeViewNode extends ISVGBackgroundNode<NodeKind.Creative>, ISize {
    readonly elements: OneOfViewElements[];
}

export interface NodeWithKind<Kind extends NodeKind> {
    readonly kind: Kind;
}

export interface ISVGBackgroundNode<Kind extends NodeKind = NodeKind> extends ISize, NodeWithKind<Kind> {
    fill?: IColor;
    border?: IBorder;
    shadows?: IShadow[];
    filters?: IFilterMap;
    radius?: number;
    rotationZ?: number;
    __rootElement?: HTMLDivElement;
    __svgBackground?: SVGBackground;
}

/**
 * The view element. I.E computed state of an element at a certain point in time
 * We should probably not explicitly set a nodekind here as that should be taken care of by the caller
 * it is however easier to do it this way since I can't be bothered to change all the places it exists
 */
export interface IElementViewNode<Kind extends NodeKind = NodeKind> extends INodeId, IPosition, ISize, ISVGBackgroundNode<Kind> {
    name?: string;
    ratio?: number;
    originX?: number;
    originY?: number;
    rotationX?: number;
    rotationY?: number;
    rotationZ?: number;
    mirrorX?: boolean;
    mirrorY?: boolean;
    scaleX?: number;
    scaleY?: number;
    opacity?: number;
    time: number;
    duration: number;
    hidden?: boolean;
    locked?: boolean;
    perspective?: number;
    filter?: IFilter[];
    feed?: IFeed;
    elementCid?: string;

    /**
     * Safari overlaps elements if they do not have their own stacking context.
     * Only way to create this seems to be to create a parent div with transform: 'translate(0)'.
     */
    __safari3dElement?: HTMLDivElement;

    /**
     * The DOM-element of the element
     */
    __rootElement?: HTMLDivElement;
    __data: Extract<OneOfElementDataNodes, { kind: Kind }>;
    __actionListeners?: IActionListener[];
}

export type OneOfElementDataNodes =
    IRectangleElementDataNode |
    IEllipseElementDataNode |
    ITextElementDataNode |
    IButtonElementDataNode |
    IImageElementDataNode |
    IGroupElementDataNode |
    IWidgetElementDataNode |
    IVideoElementDataNode |
    IAnyElementDataNode;

export interface IGroupElementDataNode extends IElementDataNode<NodeKind.Group> {
    readonly elements: OneOfElementDataNodes[];
}

export interface IAnyElementDataNode extends IElementDataNode<NodeKind.AnyElement> {}

/**
 * Data node. I.E what's actually saved (all states on timeline)
 */
export interface IElementDataNode<Kind extends NodeKind = NodeKind> extends INodeId, NodeWithKind<Kind> {
    name?: string;
    ratio?: number;
    locked: boolean;
    hidden: boolean;
    feed?: IFeed;
    time: number;
    duration: number;

    // Animatable properties
    x: number;
    y: number;
    width: number;
    height: number;
    radius: number;
    mirrorX: boolean;
    mirrorY: boolean;
    scaleX: number;
    scaleY: number;
    opacity: number;
    originX: number;
    originY: number;
    rotationX: number;
    rotationY: number;
    rotationZ: number;

    // Optional properties
    fill?: IColor;
    border?: IBorder;
    shadows?: IShadow[];
    filters?: IFilterMap;

    states: IState[];
    actions: IAction[];
    animations: IAnimation[];

    __rootNode?: CreativeDataNode;
}

export type IInitializationElementNode = { -readonly [P in keyof IElementDataNode]: IElementDataNode[P] };

export interface ITextDataNode<Kind extends NodeKind = NodeKind> extends IElementDataNode<Kind> {
    content: IText;
    __content: IText;
    textColor: IColor;
    textShadows?: ITextShadow[];
    uppercase: boolean;
    underline: boolean;
    strikethrough: boolean;
    horizontalAlignment: HorizontalAlignment;
    verticalAlignment: VerticalAlignment;
    lineHeight: number;
    characterSpacing: number;
    fontSize?: number;
    maxRows?: number;
    padding?: IPadding;
    textOverflow?: TextOverflow;
    font?: IFontStyle;

    characterStyles: Map<string, Partial<ICharacterProperties>>;
    __styleHashMap: Map</* styleHash */string, /* styleId */string>;

    // We have to store the deleted style's ids to refer to them if we happen to create new style
    // with exact same styles.
    __deletedStyleHashMap: Map</* styleHash */string, /* styleId */ string>;
    __fontStyleId?: string;
    __rootElement?: HTMLDivElement;
    __backgroundElement?: SVGSVGElement;
    __centerElement?: HTMLDivElement;
    __textElement?: HTMLDivElement;

    // We store the dirty content to enable global undo. Pls. check docs char-styles.md.
    __dirtyContent?: IText;
}


export interface IRectangleElementDataNode extends IElementDataNode<NodeKind.Rectangle> {}
export interface IEllipseElementDataNode extends IElementDataNode<NodeKind.Ellipse> {}
export interface ITextElementDataNode extends ITextDataNode<NodeKind.Text> {}
export interface IButtonElementDataNode extends ITextDataNode<NodeKind.Button> {}

export interface IImageElementDataNode extends IElementDataNode<NodeKind.Image> {
    imageAsset?: IImageAsset;
    feed?: IFeed;
    imageSettings: IImageSettings;
}

export interface IVideoElementDataNode extends IElementDataNode<NodeKind.Video> {
    videoSettings: IVideoSettings;
    videoAsset?: IVideoAsset;
}

export interface ITextNode<Kind extends NodeKind = NodeKind> extends ITextElementProperties, ICharacterProperties, IElementViewNode<Kind> {
    content: IText;
    horizontalAlignment: HorizontalAlignment;
    verticalAlignment: VerticalAlignment;
    maxRows: number;
    padding: IPadding;
    textOverflow: TextOverflow;
    __rootElement?: HTMLDivElement;
    __centerElement?: HTMLDivElement;
    __textElement?: HTMLDivElement;
    __richTextRenderer: RichText;
    __dirtyContent?: IText;
}

export interface ITextViewElement extends ITextNode<NodeKind.Text> {}

export interface IButtonViewElement extends ITextNode<NodeKind.Button> {}

export interface IEllipseViewElement extends IElementViewNode<NodeKind.Ellipse> {}

export interface IRectangleViewElement extends IElementViewNode<NodeKind.Rectangle> {}

export interface IAnyViewElement extends IElementViewNode<NodeKind.AnyElement> {}

export interface IImageViewElement extends IElementViewNode<NodeKind.Image> {
    imageUrl?: string;

    // Should view element have this property?
    feed?: IFeed;
}

export interface IVideoViewElement extends IElementViewNode<NodeKind.Video> {
    __videoRenderer: IVideoRenderer;
}
